import type { AuthIdentity, SecurityCheckResult } from '@luacentrix/contracts';

export const SECURITY_PERMISSIONS = [
  'security.read',
  'security.block',
  'security.allowlist',
  'security.policy',
  'security.platform',
] as const;

export type SecurityPermission = (typeof SECURITY_PERMISSIONS)[number];

export type PermissionDecisionReason = SecurityCheckResult['reason'];

export interface SecurityDecision {
  allowed: boolean;
  reason: PermissionDecisionReason;
  permission: SecurityPermission;
}

export const SECURITY_PERMISSION_LABELS: Record<SecurityPermission, string> = {
  'security.read': 'Read',
  'security.block': 'Blocks',
  'security.allowlist': 'Allowlist',
  'security.policy': 'Policy',
  'security.platform': 'Platform',
};

export const DECISION_SHORT_LABELS: Record<PermissionDecisionReason, string> = {
  allowed: 'granted',
  no_roles: 'no roles',
  missing_permission: 'not granted',
  explicit_deny: 'denied',
  expired: 'expired',
};

export function isSecurityPermission(value: string): value is SecurityPermission {
  return (SECURITY_PERMISSIONS as readonly string[]).includes(value);
}

export function grantedSecurityPermissions(
  identity: Pick<AuthIdentity, 'permissions' | 'denied_permissions'> | null,
): SecurityPermission[] {
  if (identity === null) return [];
  const granted = new Set<SecurityPermission>();
  const denied = new Set<SecurityPermission>();
  for (const value of identity.permissions) {
    if (isSecurityPermission(value)) granted.add(value);
  }
  for (const value of identity.denied_permissions) {
    if (isSecurityPermission(value)) denied.add(value);
  }
  for (const value of denied) granted.delete(value);
  return SECURITY_PERMISSIONS.filter(permission => granted.has(permission));
}

export function checkSecurityPermission(
  identity: Pick<AuthIdentity, 'permissions' | 'denied_permissions' | 'expires_at'> | null,
  permission: SecurityPermission,
  reference: number,
): SecurityDecision {
  if (identity === null) {
    return { allowed: false, reason: 'no_roles', permission };
  }
  if (identity.expires_at !== null && identity.expires_at <= reference) {
    return { allowed: false, reason: 'expired', permission };
  }
  const granted: readonly string[] = identity.permissions;
  const denied: readonly string[] = identity.denied_permissions;
  if (denied.includes(permission)) {
    return { allowed: false, reason: 'explicit_deny', permission };
  }
  if (granted.length === 0) {
    return { allowed: false, reason: 'no_roles', permission };
  }
  return granted.includes(permission)
    ? { allowed: true, reason: 'allowed', permission }
    : { allowed: false, reason: 'missing_permission', permission };
}
